/**
 * Extract audio track from a video File and re-encode as a 16-bit mono WAV
 * Blob suitable for upload to Whisper. Pure browser APIs.
 *
 * Uses AudioContext.decodeAudioData on the entire file (acceptable for the
 * demo target of <100MB videos) and writes a minimal RIFF/WAVE container.
 *
 * Returns null if the video has no decodable audio track.
 */
export async function extractAudioAsWav(
  file: File,
  targetSampleRate = 16000,
): Promise<Blob | null> {
  const arrayBuffer = await file.arrayBuffer();

  // AudioContext can decode mp4/m4a/webm/mp3 audio tracks via its bundled
  // demuxer. Some browsers refuse 0-channel tracks — we treat those as
  // "no audio" and return null.
  const Ctx: typeof AudioContext =
    (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext);
  if (!Ctx) throw new Error("audio_context_unavailable");

  const ctx = new Ctx();
  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  } catch {
    await ctx.close().catch(() => {});
    return null;
  }
  await ctx.close().catch(() => {});

  if (!buffer || buffer.length === 0 || buffer.numberOfChannels === 0) return null;

  // Resample + downmix to mono via OfflineAudioContext (faster than realtime).
  const offline = new OfflineAudioContext(
    1,
    Math.ceil(buffer.duration * targetSampleRate),
    targetSampleRate,
  );
  const src = offline.createBufferSource();
  src.buffer = buffer;
  // Mix to mono by averaging channels (default ChannelMergerNode would just
  // pass-through; explicit gain summing keeps it predictable).
  const merger = offline.createGain();
  merger.gain.value = 1 / Math.max(buffer.numberOfChannels, 1);
  src.connect(merger);
  merger.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();

  return audioBufferToWav(rendered);
}

/**
 * Encode an AudioBuffer as a 16-bit PCM mono RIFF/WAVE Blob.
 * Adapted from Mozilla's MDN reference implementation.
 */
export function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = 1; // forced mono
  const sampleRate = buffer.sampleRate;
  const samples = buffer.getChannelData(0);
  const blockAlign = (numChannels * 16) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataLength = samples.length * blockAlign;
  const headerLength = 44;
  const totalLength = headerLength + dataLength;

  const arrayBuffer = new ArrayBuffer(totalLength);
  const view = new DataView(arrayBuffer);

  let offset = 0;
  function writeString(s: string) {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  }
  function writeUint32(v: number) {
    view.setUint32(offset, v, true);
    offset += 4;
  }
  function writeUint16(v: number) {
    view.setUint16(offset, v, true);
    offset += 2;
  }

  // RIFF header
  writeString("RIFF");
  writeUint32(totalLength - 8);
  writeString("WAVE");
  // fmt chunk
  writeString("fmt ");
  writeUint32(16); // chunk size
  writeUint16(1); // PCM
  writeUint16(numChannels);
  writeUint32(sampleRate);
  writeUint32(byteRate);
  writeUint16(blockAlign);
  writeUint16(16); // bits per sample
  // data chunk
  writeString("data");
  writeUint32(dataLength);

  // PCM samples
  let pos = offset;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    pos += 2;
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
}
